import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { AppDispatch } from "src/redux/store";
import {
  getReasonListAsync,
  createReasonDataAsync,
  updateReasonDetailsAsync,
  deleteReasonDataAsync,
} from "src/redux/actions/omsPageActions";
import { KTIcon } from "src/marketConnect/helpers";
import CommonAlertModal from "src/utils/modals/CommonAlertModal";

interface Reason {
  id: number;
  api_name: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  onSelect: (reason: { label: string; value: number }) => void;
}

const ManageReasonModal: React.FC<Props> = ({ show, onClose, onSelect }) => {
  const dispatch = useDispatch<AppDispatch>();

  const [reasons, setReasons] = useState<Reason[]>([]);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);


  /* -------------------- LOAD REASONS -------------------- */
  const loadReasons = async () => {
    try {
      const res: any = await dispatch(
        getReasonListAsync({
          paramData: { page: 1, limit: 50, isActive: true },
        })
      );

      setReasons(res?.payload?.results?.data || []);
    } catch {
      toast.error("Failed to load reasons");
    }
  };

  useEffect(() => {
    if (show) {
      loadReasons();
      setName("");
      setEditingId(null);
    }
  }, [show]);

  /* -------------------- SAVE (CREATE / UPDATE) -------------------- */
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Reason is required");
      return;
    }

    setLoading(true);

    try {
      if (editingId) {
        await dispatch(
          updateReasonDetailsAsync({
            id: editingId,
            payloadupdate: { name },
          })
        );

        toast.success("Reason updated");
      } else {
        const res: any = await dispatch(
          createReasonDataAsync({ api_name: name })
        );

        toast.success("Reason created");

        // auto-select newly created reason
        onSelect({
          label: res?.payload?.data?.name ?? name,
          value: res?.payload?.data?.id,
        });

        onClose();
      }

      setName("");
      setEditingId(null);
      // loadReasons();
    } catch {
      toast.error("Failed to save reason");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- DELETE -------------------- */
  const handleDeleteConfirmed = async () => {
    if (!pendingDeleteId) return;
    setDeleteLoading(true);
    try {
      const res = await dispatch(deleteReasonDataAsync({ id: pendingDeleteId }));
      console.log("Delete response", res?.payload);
      if (res?.payload?.status === 200) {
        toast.success(res?.payload?.message || "Reason marked inactive");
        loadReasons();
      } else {
        toast.error(res?.payload?.data?.message || "Failed to delete reason");
      }
    } catch {
      toast.error("Failed to delete reason");
    } finally {
      setDeleteLoading(false);
      setPendingDeleteId(null);
      setShowDeleteModal(false);
    }
  };


  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Manage Adjustment Reasons</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* INPUT */}
        <label className="fw-semibold mb-1">Reason*</label>
        <input
          className="form-control mb-3"
          placeholder="Enter reason"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        {/* ACTION BUTTONS */}
        <div className="d-flex gap-2 mb-4">
          <button
            className="btn btn-primary d-flex align-items-center justify-content-center"
            disabled={loading}
            style={{ height: "34px" }}
            onClick={handleSave}
          >
            {editingId ? "Update" : "Save and Select"}
          </button>

          <button
            className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
            style={{ height: "34px" }}
            onClick={() => {
              setName("");
              setEditingId(null);
            }}
          >
            Clear
          </button>
        </div>

        {/* LIST */}
        <div className="fw-bold text-muted mb-2">REASONS</div>

        {reasons.length === 0 && (
          <div className="text-muted">No reasons found</div>
        )}

        {reasons.map((r) => (
          <div
            key={r.id}
            className="d-flex justify-content-between align-items-center py-2 border-bottom"
          >
            <span
              style={{ cursor: "pointer" }}
              onClick={() => {
                setName(r.api_name);
                setEditingId(r.id);
              }}
            >
              {r.api_name}
            </span>

            <span
              className="text-danger d-flex align-items-center gap-1"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setPendingDeleteId(r.id);
                setShowDeleteModal(true);
              }}
            >
              Mark Inactive
              <KTIcon iconName="trash" className="fs-6" />
            </span>
          </div>
        ))}

      </Modal.Body>

      <CommonAlertModal
        title="Delete Reason?"
        subtitle="This reason will be marked as inactive."
        isVisible={showDeleteModal}
        isTermsVisible={false}
        onSuccess={handleDeleteConfirmed}
        onCancel={() => {
          setPendingDeleteId(null);
          setShowDeleteModal(false);
        }}
        successBtnTitle="Delete"
        cancelBtnTitle="Cancel"
        successBtnClasses="btn btn-sm btn-danger m-2"
        successDisabled={deleteLoading}
      />
    </Modal>
  );
};

export default ManageReasonModal;
